import { ValidationErrorInfo, ValidationErrorType, type BaseEntity, type UserInfo, type ValidationResult } from '@memberjunction/core';
import { DecideForeignKeysUnchanged, type OEPImmutableEntityKey, OEP_IMMUTABLE_COLUMNS, type OEPRuleRow, type OEPRuleScalar, type RuleDecision } from '../rules/decisions.js';
import { CreateOEPRuleData, type IOEPRuleData } from '../rules/data.js';
import { IsStaffOrSystem } from '../rules/user.js';
import { OEP_ENTITY } from '../contracts/entities.js';

/** Adds a failed decision to the result. */
export function Apply<T>(result: ValidationResult, d: RuleDecision<T>, value: OEPRuleScalar): void {
    if (!d.Ok) {
        result.Errors.push(new ValidationErrorInfo(d.Field, d.Message, value, ValidationErrorType.Failure));
        result.Success = false;
    }
}

/** Everything a rule wrapper needs about the save: lookups as the saving user, who they are, and the fresh DB row. */
export interface OEPRuleContext {
    Data: IOEPRuleData;
    IsStaff: boolean;
    SaverUserID: string;
    /** Fresh DB read of the immutable columns (+ extras); null for a new record or an unreadable one. */
    Current: OEPRuleRow | null;
}

/**
 * Builds the rule context (as `saver`, the entity's BaseEntity.ActiveUser) and applies the FK-immutability decision (compared against a fresh DB read,
 * never BaseEntity OldValue). Returns null when the save must stop here (update of an unreadable row).
 */
export async function BeginRules(
    entity: BaseEntity, saver: UserInfo, result: ValidationResult, key: OEPImmutableEntityKey, id: string,
    proposed: OEPRuleRow, extraFields: readonly string[] = [], selfStampFields: readonly string[] = [],
): Promise<OEPRuleContext | null> {
    // `saver` is the entity's BaseEntity.ActiveUser: ContextCurrentUser on the server (set per request), the
    // provider's CurrentUser in the browser, where ContextCurrentUser is never set (T33b: a presenter's own
    // message was refused "You can only save this as yourself." because the client saver was '').
    const user = saver;
    const Data = CreateOEPRuleData(entity.RunViewProviderToUse, user);
    const IsStaff = IsStaffOrSystem(user);
    const fields: readonly string[] = OEP_IMMUTABLE_COLUMNS[key];
    const Current = entity.IsSaved ? await Data.CurrentRow(OEP_ENTITY[key], id, [...new Set([...fields, ...selfStampFields, ...extraFields])]) : null;
    const d = DecideForeignKeysUnchanged({
        IsStaffOrSystem: IsStaff, IsNew: !entity.IsSaved, Current, Proposed: proposed,
        Fields: fields, SelfStampFields: selfStampFields, SaverUserID: user?.ID ?? '',
    });
    Apply(result, d, d.Ok ? null : (proposed[d.Field] ?? null));
    if (entity.IsSaved && !Current && !IsStaff) return null;
    return { Data, IsStaff, SaverUserID: user?.ID ?? '', Current };
}

/** The ID a rule should trust: the DB value for non-staff on update, the proposed value otherwise. */
export function TrustedID(ctx: OEPRuleContext, field: string, proposed: string): string {
    const v = ctx.Current?.[field];
    return !ctx.IsStaff && typeof v === 'string' ? v : proposed;
}

/** A column from the fresh DB row as a string, or null (new record / null column). */
export function CurrentString(ctx: OEPRuleContext, field: string): string | null {
    const v = ctx.Current?.[field];
    return v === null || v === undefined ? null : String(v);
}
